// Starting or ending with one or more /, space or word boundary
const urlTidyRegex = /(^[\s\b/]+)|([/\s\b]+$)/gim

/**
 * Appends a query parameter to a URL if the value exists and the parameter isn't already present.
 */
export function appendQueryParam(
  url: string,
  key: string,
  value: string | undefined
): string {
  const hasValue = value && value !== 'undefined'
  if (!hasValue || new RegExp(`([?&])${key}=`).test(url)) {
    return url
  }
  const separator = url.includes('?') ? '&' : '?'
  return `${url}${separator}${key}=${value}`
}

/**
 * Generates a Portal URL to a specific path.
 * This utilty function allows for differences in PORTAL_URL or path lead/trailing spaces/slashes.
 *
 * @param {string} path
 * @returns {string}
 */
export function toPortalUrl(path: string): string {
  const PORTAL_URL = import.meta.env.GLP_PORTAL_URL.replace(urlTidyRegex, '')
  const trimPath = path.replace(urlTidyRegex, '')
  const baseUrl = `${PORTAL_URL}/${trimPath}`

  return appendQueryParam(baseUrl, 'site_id', import.meta.env.GLP_SITE_ID)
}

/**
 * Generates a GLX URL to a specific path.
 * This utilty function allows for differences in GLX_URL or path lead/trailing spaces/slashes.
 *
 * @param {string} path
 * @returns {string}
 */
export function toGlxUrl(path: string): string {
  const GLX_URL = import.meta.env.GLP_GLX_URL.replace(urlTidyRegex, '')
  const trimPath = path.replace(urlTidyRegex, '')
  return `${GLX_URL}/${trimPath}`
}

/**
 * Generates a Checkout URL to a specific path.
 * This utilty function allows for differences in API_URL or path lead/trailing spaces/slashes.
 *
 * @param {string} path
 * @returns {string}
 */
export function toApiUrl(path: string): string {
  const API_URL = import.meta.env.GLP_API_URL.replace(urlTidyRegex, '')
  const trimPath = path.replace(urlTidyRegex, '')
  const baseUrl = `${API_URL}/${trimPath}`

  return appendQueryParam(baseUrl, 'site_id', import.meta.env.GLP_SITE_ID)
}

/**
 * Generates a full URL for this site.
 * This utilty function allows for differences in API_URL or path lead/trailing spaces/slashes.
 *
 * @param {string} path
 * @returns {string}
 */
export function toFullUrl(path: string = ''): string {
  const hostname = location.origin.replace(urlTidyRegex, '')
  const trimPath = path.replace(urlTidyRegex, '')
  return `${hostname}/${trimPath}`
}

/**
 * Generates a GLIDE url to specific path.
 * This utilty function allows for differences in API_URL or path lead/trailing spaces/slashes.
 *
 * @param {string} path
 * @returns {string}
 */
export function toGlideUrl(path: string): string {
  const API_URL = import.meta.env.GLP_GLIDE_URL.replace(urlTidyRegex, '')
  const trimPath = path.replace(urlTidyRegex, '')
  return `${API_URL}/${trimPath}`
}

/**
 * Generates a CMS URL to a specific path.
 * This utilty function allows for differences in CMS_URL or path lead/trailing spaces/slashes.
 * Automatically includes the tenant and locale parameters from environment configuration.
 *
 * @param {string} path
 * @returns {string}
 */
export function toCmsUrl(path: string): string {
  const CMS_URL = import.meta.env.GLP_CMS_URL?.replace(urlTidyRegex, '')
  const trimPath = path.replace(urlTidyRegex, '')
  let url = `${CMS_URL}/${trimPath}`
  url = appendQueryParam(url, 'tenant', import.meta.env.GLP_TENANT)
  url = appendQueryParam(url, 'site_id', import.meta.env.GLP_SITE_ID)
  url = appendQueryParam(url, 'locale', import.meta.env.GLP_LOCALE)

  return url
}

/**
 * Generates a Cloudflare asset URL for a given path.
 *
 * @param {string} path The path to the asset.
 * @returns {string} The full URL to the Cloudflare asset.
 */
export function toCfAssetUrl(path: string): string {
  const CLOUDFLARE_ASSET_URL =
    import.meta.env.GLP_CLOUDFLARE_ASSET_URL?.replace(urlTidyRegex, '')

  const trimPath = path.replace(urlTidyRegex, '')
  return `${CLOUDFLARE_ASSET_URL}/${trimPath}`
}

type CFResizeOptions = {
  width?: number
  height?: number
  sharpen?: number
  quality?: number
}

/**
 * Generates a Cloudflare image resizing URL for a given image and width/height.
 *
 * @see https://developers.cloudflare.com/images/optimization/features/
 * @param {string} imageUrl
 * @param {Object} options
 * @param {number} [options.width]
 * @param {number} [options.height]
 * @param {number} [options.sharpen]
 * @param {number} [options.quality]
 * @returns {string}
 */
export function toCfResizeUrl(
  imageUrl: string,
  { width, height, sharpen, quality }: CFResizeOptions
): string {
  return imageUrl

  // Guard against non-Cloudflare URLs
  if (
    !imageUrl?.startsWith('https://assets') ||
    import.meta.env.MODE !== 'production'
  ) {
    return imageUrl
  }

  const mutations = [`sharpen=${sharpen ?? 0.75}`, `quality=${quality ?? 100}`]

  if (width) {
    mutations.push(`width=${width}`)
  }

  if (height) {
    mutations.push(`height=${height}`)
  }

  return `${window.location.origin}/cdn-cgi/image/${mutations.join(',')}/${imageUrl}`
}
